import { Geodesic } from "geographiclib-geodesic";

// Initial bearing between two GPS points on the WGS84 ellipsoid.
// Returns an angle in degrees in [0, 360[: 0 = North, 90 = East, 180 = South, 270 = West.
export const getBearing = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const { azi1 } = Geodesic.WGS84.Inverse(lat1, lon1, lat2, lon2);
  return ((azi1 ?? 0) + 360) % 360;
};

// Returns true if the vehicle is currently on a trip (last data point is more recent than all completed trip end dates).
export const isTripOngoing = (
  lastDataPointDate: string | undefined,
  completedTrips: { endDate: string }[],
): boolean => {
  if (!lastDataPointDate) return false;
  if (completedTrips.length === 0) return true;
  const lastEndDate = Math.max(
    ...completedTrips.map((t) => new Date(t.endDate).getTime()),
  );
  return new Date(lastDataPointDate).getTime() > lastEndDate;
};

// Bearing from the last two data points — used to orient the car icon when a trip is ongoing.
export const getCarBearing = (
  dataPoints: { lat: number; lon: number }[],
): number => {
  if (dataPoints.length < 2) return 0;
  const p1 = dataPoints[dataPoints.length - 2];
  const p2 = dataPoints[dataPoints.length - 1];
  return getBearing(p1.lat, p1.lon, p2.lat, p2.lon);
};

type DataPoint = { lat: number; lon: number; date: string };

// Index of the data point closest in time to the given date. Ties keep the earliest point.
// Single source of truth for a trip boundary, shared by getJunctionDataPoints and
// getTripDataPoints so the highlighted segment always reaches the junction marker.
const getClosestDataPointIndex = (
  dataPoints: { date: string }[],
  date: string,
): number => {
  const target = new Date(date).getTime();
  let closestIndex = 0;
  let closestDelta = Infinity;
  for (let i = 0; i < dataPoints.length; i++) {
    const delta = Math.abs(new Date(dataPoints[i].date).getTime() - target);
    if (delta < closestDelta) {
      closestDelta = delta;
      closestIndex = i;
    }
  }
  return closestIndex;
};

// For each trip boundary (except the last), returns the data point closest in time to the trip's end date.
// Used to place the junction circle icon between consecutive trips.
export const getJunctionDataPoints = <T extends DataPoint>(
  dataPoints: T[],
  trips: { endDate: string }[],
): T[] => {
  if (dataPoints.length === 0 || trips.length === 0) return [];
  const lastDataPointTime = new Date(
    dataPoints[dataPoints.length - 1].date,
  ).getTime();
  const lastTrip = trips[trips.length - 1];
  const includeLastTrip =
    lastDataPointTime > new Date(lastTrip.endDate).getTime();
  const tripsToMark = includeLastTrip ? trips : trips.slice(0, -1);
  return tripsToMark.map(
    (trip) => dataPoints[getClosestDataPointIndex(dataPoints, trip.endDate)],
  );
};

// Data points forming a trip's highlighted (green) segment. Spans from the previous trip's
// junction (or the first data point for the first trip) to this trip's own junction — the data
// point closest to its end date — boundaries INCLUSIVE. Boundaries use getClosestDataPointIndex
// — the same rule as getJunctionDataPoints — so consecutive segments share their junction point,
// the green line always reaches the junction marker, and it never overshoots past the trip's end
// onto the following data points when the vehicle keeps moving afterwards (ongoing trip).
export const getTripDataPoints = <T extends DataPoint>(
  trip: { startDate: string; endDate: string } | null,
  dataPoints: T[],
  trips: { startDate: string; endDate: string }[],
): T[] => {
  if (!trip || dataPoints.length === 0 || trips.length === 0) return [];
  const index = trips.findIndex(
    (t) => t.startDate === trip.startDate && t.endDate === trip.endDate,
  );
  if (index === -1) return [];
  const startIndex =
    index === 0
      ? 0
      : getClosestDataPointIndex(dataPoints, trips[index - 1].endDate);
  const endIndex = getClosestDataPointIndex(dataPoints, trips[index].endDate);
  return dataPoints.slice(startIndex, Math.max(startIndex, endIndex) + 1);
};

const ARROW_INTERVAL_M = 3000;

// Places one arrow every ARROW_INTERVAL_M, starting at half the interval.
// Falls back to the midpoint if the segment is shorter than the interval.
const getSegmentArrows = (
  points: { lat: number; lon: number }[],
): { lat: number; lon: number; bearing: number }[] => {
  if (points.length < 2) return [];
  const arrows: { lat: number; lon: number; bearing: number }[] = [];
  let accumulated = ARROW_INTERVAL_M / 2;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const { s12 } = Geodesic.WGS84.Inverse(p1.lat, p1.lon, p2.lat, p2.lon);
    accumulated += s12 ?? 0;
    if (accumulated >= ARROW_INTERVAL_M) {
      arrows.push({
        lat: p1.lat,
        lon: p1.lon,
        bearing: getBearing(p1.lat, p1.lon, p2.lat, p2.lon),
      });
      accumulated = 0;
    }
  }
  if (arrows.length === 0) {
    const midIdx = Math.floor((points.length - 1) / 2);
    const p1 = points[midIdx];
    const p2 = points[midIdx + 1];
    arrows.push({
      lat: p1.lat,
      lon: p1.lon,
      bearing: getBearing(p1.lat, p1.lon, p2.lat, p2.lon),
    });
  }
  return arrows;
};

// For each trip segment, places directional arrows every 3km.
// Falls back to one arrow at the midpoint for short segments.
// If trips is empty, applies the logic to all datapoints.
export const getArrowPositions = (
  dataPoints: DataPoint[],
  trips: { startDate: string; endDate: string }[],
): { lat: number; lon: number; bearing: number }[] => {
  if (dataPoints.length < 2) return [];
  if (trips.length === 0) {
    const sorted = [...dataPoints]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .filter(
        (dp, i, arr) =>
          i === 0 || dp.lat !== arr[i - 1].lat || dp.lon !== arr[i - 1].lon,
      );
    return getSegmentArrows(sorted);
  }
  return trips.flatMap((trip) => {
    const startTime = new Date(trip.startDate).getTime();
    const endTime = new Date(trip.endDate).getTime();
    const segmentPoints = dataPoints
      .filter((dp) => {
        const t = new Date(dp.date).getTime();
        return t >= startTime && t <= endTime;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .filter(
        (dp, i, arr) =>
          i === 0 || dp.lat !== arr[i - 1].lat || dp.lon !== arr[i - 1].lon,
      );
    return getSegmentArrows(segmentPoints);
  });
};

export const getAddressLines = (
  address: string | null | undefined,
): string[] => {
  if (!address) return [];

  const segments = address
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean);

  const lastSegment = segments[segments.length - 1];

  const endsWithCountry = segments.length > 1 && !/\d/.test(lastSegment);

  return endsWithCountry ? segments.slice(0, -1) : segments;
};

type DriverUsage = { vehicleUuid: string; startOfUsage: string };

export const getDriverUsageAtDateByVehicle = <T extends DriverUsage>(
  driverUsages: T[],
  date: Date,
): Record<string, T> => {
  const endOfSelectedDay = new Date(date);
  endOfSelectedDay.setHours(23, 59, 59, 999);

  return driverUsages.reduce(
    (acc, usage) => {
      const startOfUsage = new Date(usage.startOfUsage);
      if (startOfUsage > endOfSelectedDay) return acc;
      const known = acc[usage.vehicleUuid];
      if (
        known &&
        new Date(known.startOfUsage).getTime() >= startOfUsage.getTime()
      ) {
        return acc;
      }
      acc[usage.vehicleUuid] = usage;
      return acc;
    },
    {} as Record<string, T>,
  );
};
